import React from "react";
import { useDashboardData } from "@/services/dashboardDataService";
import { useTheme } from "@/contexts/ThemeContext";
import { useWidgetStatusReporter } from "@/contexts/DashboardWidgetContext";

// Axis labels for the heatmap
const LIKELIHOOD = ["Rare", "Unlikely", "Possible", "Likely", "Almost Certain"];
const CONSEQUENCE = ["Insignificant", "Minor", "Moderate", "Major", "Catastrophic"];

// Default empty heatmap data
const EMPTY_HEATMAP = Array(5).fill(0).map(() => Array(5).fill(0));

// Interface for the risk data from the backend
interface RiskHeatmapData {
  heatmap: Record<string, number>; // Maps cell classes to counts (e.g., 'OneXThree' -> 2)
  risks: Array<{
    id: string;
    residualRisk: number;
    ICXCE: number;
    ILXCE: number;
    reduction: 'likelihood' | 'consequence';
    cellClass: string;
    riskData: any;
  }>;
  totalRisks: number;
  lastUpdated: string;
}

const CELL_COLORS = {
  light: [
    "bg-green-200", // Low
    "bg-yellow-200", // Moderate
    "bg-orange-200", // High
    "bg-red-400", // Very High
    "bg-red-700 text-white", // Extreme
  ],
  dark: [
    "bg-green-900/30 border border-green-600/30", // Low
    "bg-yellow-900/30 border border-yellow-600/30", // Moderate
    "bg-orange-900/30 border border-orange-600/30", // High
    "bg-red-900/40 border border-red-600/40", // Very High
    "bg-red-900/70 border border-red-600/70 text-red-100", // Extreme
  ]
};

const EMPTY_CELL_CLASS = {
  light: "bg-gray-100",
  dark: "bg-gray-800/30 border border-gray-700/30"
};

function getCellColor(count: number, x: number, y: number, isDark: boolean) {
  const theme = isDark ? 'dark' : 'light';
  const colors = CELL_COLORS[theme];
  
  if (count === 0) return EMPTY_CELL_CLASS[theme];
  if (x + y <= 1) return colors[0];
  if (x + y === 2) return colors[1];
  if (x + y === 3) return colors[2];
  if (x + y === 4) return colors[3];
  return colors[4];
}

const RiskHeatMapWidget: React.FC = () => {
  const cellCount = 5;
  const cellGap = 4; // px
  const { isDark } = useTheme();
  
  const { data, isLoading, error, metadata } = useDashboardData('risk-heatmap');

  // Report widget status to dashboard context
  useWidgetStatusReporter('risk-heatmap', isLoading, error, !!data);
  
  // Transform the heatmap data from the backend to the 5x5 grid format
  const heatmapData = React.useMemo(() => {
    if (!data?.heatmap) return EMPTY_HEATMAP;
    
    // Initialize a 5x5 grid with zeros
    const grid: number[][] = Array(5).fill(0).map(() => Array(5).fill(0));
    
    // Map the cell classes to grid positions
    const cellClassToPosition: Record<string, {x: number, y: number}> = {
      // Format: 'OneXOne' -> {x: 0, y: 0} (top-left, lowest risk)
      //         'FiveXFive' -> {x: 4, y: 4} (bottom-right, highest risk)
      'OneXOne': {x: 0, y: 0}, 'OneXTwo': {x: 1, y: 0}, 'OneXThree': {x: 2, y: 0}, 'OneXFour': {x: 3, y: 0}, 'OneXFive': {x: 4, y: 0},
      'TwoXOne': {x: 0, y: 1}, 'TwoXTwo': {x: 1, y: 1}, 'TwoXThree': {x: 2, y: 1}, 'TwoXFour': {x: 3, y: 1}, 'TwoXFive': {x: 4, y: 1},
      'ThreeXOne': {x: 0, y: 2}, 'ThreeXTwo': {x: 1, y: 2}, 'ThreeXThree': {x: 2, y: 2}, 'ThreeXFour': {x: 3, y: 2}, 'ThreeXFive': {x: 4, y: 2},
      'FourXOne': {x: 0, y: 3}, 'FourXTwo': {x: 1, y: 3}, 'FourXThree': {x: 2, y: 3}, 'FourXFour': {x: 3, y: 3}, 'FourXFive': {x: 4, y: 3},
      'FiveXOne': {x: 0, y: 4}, 'FiveXTwo': {x: 1, y: 4}, 'FiveXThree': {x: 2, y: 4}, 'FiveXFour': {x: 3, y: 4}, 'FiveXFive': {x: 4, y: 4},
    };

    // Populate the grid with the counts from the backend
    for (const [cellClass, count] of Object.entries(data.heatmap)) {
      const position = cellClassToPosition[cellClass];
      if (position && typeof count === 'number') {
        // Note: We need to transpose the grid because the widget expects [y][x] indexing
        grid[position.y][position.x] = count;
      }
    }

    return grid;
  }, [data]);

  return (
    <div
      className="h-full w-full flex flex-col items-center justify-center"
      style={{ containerType: "inline-size", padding: "min(2cqw, 1rem)" }}
    >
      <h3 className="font-semibold mb-4 text-center">Risk Heat Map</h3>
      <div className="flex-1 flex flex-col items-center justify-center w-full">
        {/* Axis labels and grid container */}
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'center', width: '100%', height: '100%' }}>
          {/* Y-axis (row) labels */}
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', marginRight: 8 }}>
            {LIKELIHOOD.map((l, i) => (
              <div key={l} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', fontSize: 'min(3cqw, 1.1rem)', paddingRight: 2 }}>{l}</div>
            ))}
          </div>
          {/* Grid */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${cellCount}, 1fr)`,
              gridTemplateRows: `repeat(${cellCount}, 1fr)`,
              gap: cellGap,
              width: '100%',
              height: '100%',
              background: 'transparent',
              alignItems: 'center',
              justifyItems: 'center',
              borderRadius: 8,
              flex: 1,
            }}
          >
            {heatmapData.flatMap((row, y) =>
              row.map((count, x) => (
                <div
                  key={`${y}-${x}`}
                  className={`flex items-center justify-center text-sm font-medium transition-all duration-200 ${
                    getCellColor(count, x, y, isDark)
                  } hover:opacity-90 cursor-pointer`}
                  style={{
                    width: '100%',
                    height: '100%',
                    borderRadius: 4,
                    fontSize: 'min(3cqw, 1.1rem)',
                    minWidth: 0,
                    minHeight: 0,
                  }}
                  title={`Risks: ${count}`}
                >
                  {count > 0 ? count : ""}
                </div>
              ))
            )}
          </div>
        </div>
        {/* X-axis (column) labels */}
        <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', width: '100%', maxWidth: 700, marginTop: 8, marginLeft: 'calc(8px + 2px)' }}>
          <div style={{ width: 60 }} /> {/* Spacer for y-axis */}
          {CONSEQUENCE.map((c, i) => (
            <div key={c} style={{ flex: 1, textAlign: 'center', fontSize: 'min(3cqw, 1.1rem)' }}>{c}</div>
          ))}
        </div>
      </div>
      {isLoading && (
        <div className="mt-2 text-center text-sm text-muted-foreground">
          Loading risk data...
        </div>
      )}
      {(error || metadata?.error) && (
        <div className="mt-2 text-center text-sm text-red-500">
          {error || metadata?.message || 'Failed to load risk data'}
        </div>
      )}
      <div className="mt-2 text-xs text-muted-foreground scalable-text-small text-center" style={{ fontSize: 'min(3cqw, 1.1rem)' }}>
        Higher risk bottom-right
      </div>
    </div>
  );
};
export default RiskHeatMapWidget;
